import { overrideLogMessage } from "./TemplateEditor";
import { DriveFolderUpload as UploadIcon } from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as ids from "common/ids";
import * as logger from "common/logger";
import Component from "components/Component";
import TemplateValidationDialog from "components/common/TemplateValidationDialog";
import * as config from "config";
import * as fileUtils from "fileUtils";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  toStructuredError,
  useUpdateStaticDataMutation,
} from "services/adminService";

const countErrors = (reports) =>
  (reports ?? []).reduce(
    (total, report) => total + (report.errors ?? []).length,
    0
  );

const Progress = ({ value }) => (
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <Box sx={{ width: "100%", mr: 1 }}>
      <LinearProgress variant={"determinate"} value={value} />
    </Box>
    <Box sx={{ minWidth: 35 }}>
      <Typography variant={"body2"}>{`${Math.round(value)}%`}</Typography>
    </Box>
  </Box>
);

const BulkEndorsementUpload = ({
  disabled,
  templates,
  onUpdate,
  companyId,
  reason,
  setReason,
}) => {
  const [status, setStatus] = useState({});
  // A save the server refused for template errors, held until the user
  // either overrides it or gives up: {reports, unvalidated, data}.
  const [pendingOverride, setPendingOverride] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");

  const [
    updateStaticData,
    { isLoading: isStaticDataUpdating },
  ] = useUpdateStaticDataMutation();

  const saveTemplates = async ({ data, userLogMessage, force }) => {
    const result = await updateStaticData({
      companyId,
      key: "file_templates",
      data,
      userLogMessage,
      ...(force && { force: true }),
    });
    const error =
      result?.error != null ? toStructuredError(result.error) : null;
    if (error?.type === "TEMPLATE_VALIDATION_FAILED") {
      setPendingOverride({
        reports: error.body?.reports ?? [],
        unvalidated: error.body?.unvalidated ?? [],
        data,
      });
      return false;
    }
    if (error != null) {
      throw new Error(error.message);
    }
    return true;
  };

  const handleOverrideProceed = async () => {
    const pending = pendingOverride;
    setPendingOverride(null);
    try {
      setStatus({ loading: true, message: "Saving endorsements" });
      const saved = await saveTemplates({
        data: pending.data,
        userLogMessage: overrideLogMessage(
          countErrors(pending.reports),
          overrideReason
        ),
        force: true,
      });
      if (saved) {
        setOverrideReason("");
        await onUpdate();
      }
      setStatus({});
    } catch (e) {
      logger.exception(e);
      setStatus({
        error: `Error uploading files - ${e}`,
      });
    }
  };

  const isDisabled =
    disabled ||
    status?.loading ||
    isStaticDataUpdating ||
    (config.ENVIRONMENT !== "dev" && (reason?.length ?? 0) < 4);

  const onDrop = async (files) => {
    setStatus({});
    if (!files?.length) {
      logger.info("Dropped no files. Skipping.");
      return;
    }
    try {
      setStatus({
        loading: true,
        completed: 0,
        total: files.length,
      });
      const updates = await Promise.all(
        files
          .filter((file) => file.name?.trim()?.length)
          .map(async (file) => {
            const fileId = await fileUtils.createFileEntry(
              file,
              false,
              null,
              companyId
            );
            setStatus((status) => ({
              ...status,
              completed: (status.completed ?? 0) + 1,
            }));
            return {
              active: true,
              title: file.name.split(/\s/)[0],
              filename: file.name,
              form: "WORD_JINJA_ENDORSEMENT",
              fileId,
            };
          })
      );

      const updatedTemplates = [
        ...(templates ?? []).map((template) => {
          const update = updates.find(
            ({ title, form }) =>
              title === template.title && form === template.form
          );
          return update != null ? { ...template, ...update } : template;
        }),
        ...updates
          .filter(
            ({ title, form }) =>
              templates?.find(
                (template) => title === template.title && form === template.form
              ) == null
          )
          .map((template) => ({
            ...template,
            id: ids.makeNewId(),
          })),
      ];

      const saved = await saveTemplates({
        data: updatedTemplates,
        userLogMessage: reason,
      });
      if (saved) {
        await onUpdate();
      }
      setStatus({});
    } catch (e) {
      logger.exception(e);
      setStatus({
        error: `Error uploading files - ${e}`,
      });
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    disabled: isDisabled,
    onDrop,
  });

  return (
    <Component
      title={"Bulk Endorsement Upload"}
      subtitle={
        "All files dropped here will be added / updated as Word (Jinja) Endorsements. The first word of the dropped filename will be used as the endorsement's name."
      }
    >
      <Stack direction={"column"} spacing={2}>
        <TextField
          type={"text"}
          InputLabelProps={{ shrink: true }}
          InputProps={{ spellCheck: "false" }}
          label={"Reason for change"}
          autoComplete={"off"}
          variant={"filled"}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
        />
        <div
          {...getRootProps({
            className: `dropzone ${isDragActive ? "active" : ""} ${
              isDragAccept ? "accept" : ""
            } ${isDragReject ? "reject" : ""}`,
          })}
          style={{ marginBottom: 16 }}
        >
          <input {...getInputProps()} />
          <UploadIcon fontSize={"large"} />
          <p className={"message"}>
            {"Drop .docx files here to upload as Word (Jinja) Endorsements"}
          </p>
          <Divider style={{ width: "100%", marginBottom: 16 }}>{"OR"}</Divider>
          <Button
            variant={"contained"}
            disableElevation
            color={"primary"}
            disabled={isDisabled}
          >
            {"BROWSE FILES"}
          </Button>
        </div>
        {status?.loading && (
          <Alert severity={"info"} sx={{ width: "100%" }}>
            <AlertTitle>
              {status?.message ?? "Uploading endorsements"}
            </AlertTitle>
            <Progress
              value={(100 * (status?.completed ?? 5)) / (status?.total ?? 10)}
            />
          </Alert>
        )}
        {status?.error && (
          <Alert severity={"error"} sx={{ width: "100%" }}>
            {status?.error}
          </Alert>
        )}
      </Stack>
      <TemplateValidationDialog
        open={pendingOverride != null}
        onClose={() => setPendingOverride(null)}
        reports={pendingOverride?.reports ?? []}
        unvalidated={pendingOverride?.unvalidated ?? []}
        onProceed={handleOverrideProceed}
        proceedLabel={"Save anyway"}
        requireReason
        reason={overrideReason}
        onReasonChange={setOverrideReason}
      />
    </Component>
  );
};

export default BulkEndorsementUpload;
